// Section 13 and 18: Justice policy. Pardon / Restitution / Imprisonment / Flogging / Exile /
// Execution, plus "ask each case". Default restitution, ask each case on.
// - validate: execution needs a common hall and 60% settlement trust when it is set (not when it
//   is already the policy); imprisonment needs a jail.
// - deterrence: each response used in the last 4 seasons multiplies deterrence strength by its
//   table value (0.9x pardon .. 2.0x execution), each response counted once (E1-07).
// - trustChange: the Section 13 table sets the change for the offender's household, the victims
//   and everyone else when a case is resolved (`target.justice`); Devout people watching an
//   execution lose 20 instead of 10.
// Constants: config.policies.defaults.justice, config.justice, config.policies.executionUnlockTrust.
import type { Config } from '../../config';
import { mod } from '../../core/modifiers';
import { noChangeCost } from '../../core/content-helpers';
import { executionUnlocked, hasBuilding, resolvingTurn } from '../../core/queries';
import { definePolicy } from '../../core/registry';
import type {
  DeterrenceEffect,
  JusticeResponse,
  JusticeRole,
  Modifier,
  Person,
  TrustCause,
} from '../../core/types';

/** The Section 13 table's trust change for a column, or null where the table names none. */
function tableTrust(
  cfg: Config,
  response: JusticeResponse,
  role: JusticeRole,
  person: Person | undefined,
): number | null {
  const j = cfg.justice;
  switch (response) {
    case 'pardon':
      return role === 'offender'
        ? j.pardon.offenderTrust
        : role === 'victim'
          ? j.pardon.victimTrust
          : j.pardon.othersTrust;
    case 'restitution':
      return role === 'offender'
        ? null
        : role === 'victim'
          ? j.restitution.victimTrust
          : j.restitution.othersTrust;
    case 'imprisonment':
      return role === 'offender'
        ? null
        : role === 'victim'
          ? j.imprisonment.victimTrust
          : j.imprisonment.othersTrust;
    case 'flogging':
      return role === 'offender'
        ? j.flogging.offenderTrust
        : role === 'victim'
          ? j.flogging.victimTrust
          : j.flogging.othersTrust;
    case 'exile':
      return role === 'offender'
        ? null
        : role === 'victim'
          ? j.exile.victimTrust
          : j.exile.othersTrust;
    case 'execution':
      if (role === 'offender') return null;
      if (role === 'victim') return j.execution.victimTrust;
      return person?.traits.includes('devout') === true
        ? j.execution.devoutTrust
        : j.execution.othersTrust;
  }
}

/** Active deterrence effects, one per response (the latest), in response order. */
function activeEffects(effects: readonly DeterrenceEffect[], turn: number): DeterrenceEffect[] {
  const latest = new Map<JusticeResponse, DeterrenceEffect>();
  for (const e of effects) {
    if (e.untilTurn < turn) continue;
    const seen = latest.get(e.source);
    if (seen === undefined || e.untilTurn > seen.untilTurn) latest.set(e.source, e);
  }
  return [...latest.values()].sort((a, b) => (a.source < b.source ? -1 : 1));
}

/** The trust causes the crime phase applies with the justice table (`target.justice`). */
const JUSTICE_CAUSES: readonly TrustCause[] = ['punished', 'watched-harsh-punishment'];

export function justice(config: Config) {
  return definePolicy({
    id: 'justice',
    label: 'Justice',
    section: 'S18',
    group: 'justice',
    options: null,
    default: config.policies.defaults.justice,
    modifierTargets: ['deterrence', 'trustChange'],
    modifierKeys: { trustChange: JUSTICE_CAUSES },
    modifiers(ctx, target): readonly Modifier[] {
      if (target.kind === 'deterrence') {
        return activeEffects(ctx.state.deterrence, resolvingTurn(ctx.state)).map((e) =>
          mod.mul(`justice:${e.source}`, e.multiplier, `${e.source} until turn ${e.untilTurn}`),
        );
      }
      if (
        target.kind !== 'trustChange' ||
        target.justice === undefined ||
        !JUSTICE_CAUSES.includes(target.cause)
      )
        return [];
      const { response, role } = target.justice;
      const amount = tableTrust(ctx.cfg, response, role, ctx.state.people[target.personId]);
      return amount === null
        ? []
        : [mod.set(`justice:${response}`, amount, `${response}, ${role}`)];
    },
    changeCost: noChangeCost,
    // M6: what a detected offender faces, its deterrence (Section 13 table) and who decides.
    affects(ctx, option) {
      const pending = ctx.state.openPrompts.filter((p) => p.kind === 'justice').length;
      return [
        {
          key: `policy.justice.response.${option.response}`,
          params: { deterrence: ctx.cfg.justice[option.response].deterrence },
        },
        {
          key: option.askEachCase ? 'policy.justice.ask' : 'policy.justice.auto',
          params: { pending },
        },
      ];
    },
    validate(ctx, option) {
      if (option.response === 'imprisonment' && !hasBuilding(ctx.state, 'jail')) {
        return {
          path: 'policies.justice',
          code: 'policy_locked',
          message: 'Imprisonment needs a jail.',
        };
      }
      if (option.response !== 'execution') return null;
      if (ctx.state.policies.justice.response === 'execution') return null;
      return executionUnlocked(ctx.state, ctx.cfg)
        ? null
        : {
            path: 'policies.justice',
            code: 'policy_locked',
            message: 'Execution needs a common hall and settlement trust of 60.',
          };
    },
  });
}
